<!--suppress ALL -->
<template>
  <img :data-src="lazySrc"
       :data-srcset="lazySrcset"
       :data-mfp-src="lazySrc"
       :style="style" @error="onError"
       class="lazy-img"/>
</template>

<script lang="ts">
  import {Component, Vue, Prop} from 'annotation';

  @Component
  export default class LazyImg extends Vue {

    @Prop({type: String, default: '#efefef'})
    public backgroundColor: string;

    @Prop({type: Number, default: null})
    public height: number;

    @Prop({type: String, default: null})
    public lazySrc: string;

    @Prop({type: String, default: null})
    public lazySrcset: string;

    @Prop({type: Number, default: null})
    public width: number;

    @Prop({default: false, type: Boolean})
    public isMagnify: boolean;

    @Prop({default: false, type: Boolean})
    public isProfile: boolean;

    @Prop({default: false, type: Boolean})
    public isMateri: boolean;

    @Prop({default: false, type: Boolean})
    public isBookCover: boolean;

    @Prop({type: String, default: '/static/images/no-img-found.png'})
    public fallbackImage: string;

    public loading: boolean = true;

    public get aspectRatio() {
      // Calculate the aspect ratio of the image
      // if the width and the height are given.
      if (!this.width || !this.height) return null;

      return (this.height / this.width) * 100;
    }

    public get style() {
      // The background color is used as a
      // placeholder while loading the image.
      // You can use the dominant color of the
      // image to improve perceived performance.
      // See: https://manu.ninja/dominant-colors-for-lazy-loading-images/
      const style: any = {};

      if (this.width) style['width'] = `${this.width}px`;
      if (this.height) style['height'] = `${this.height}px`;
      if (this.loading) style['backgroundColor'] = this.backgroundColor;

      // If the image is still loading and an
      // aspect ratio could be calculated, we
      // apply the calculated aspect ratio by
      // using padding top.
      const applyAspectRatio = this.loading && this.aspectRatio;
      if (applyAspectRatio) {
        // Prevent flash of unstyled image
        // after the image is loaded.
        style['height'] = 0;
        // Scale the image container according
        // to the aspect ratio.
        style['paddingTop'] = `${this.aspectRatio}%`;
      }

      return style;
    }

    public mounted() {
      this.handleImage();
      this.$watch('lazySrc', () => {
        this.$el.src = this.lazySrc;
        this.$el.setAttribute('data-mfp-src', this.lazySrc);
      });

      if (!this.lazySrc) {
        this.onError();
      }
    }

    public onError() {
      const {isProfile, isMateri, isBookCover, fallbackImage} = this;
      let src: string = isProfile || isBookCover || isMateri ?
        `/static/images/${isProfile ? 'avatar/default_profile.jpg' : isMateri ? 'bg_card.jpg' : 'book-cover.png'}` : fallbackImage;

      this.$el.src = src;
    }

    public handleImage() {
      // As soon as the <img> element triggers
      // the `load` event, the loading state is
      // set to `false`, which removes the apsect
      // ratio we've applied earlier
      const setLoadingState = () => {
        this.loading = false;
      };
      this.$el.addEventListener('load', setLoadingState);

      // We remove the event listener as soon as
      // the components is destroyed to prevent
      // potential memory leaks.
      this.$once('hook:destroyed', () => {
        this.$el.removeEventListener('load', setLoadingState);
      });

      // We initialize Lozad.js on the root
      // element of our components.
      const observer = window.lozad(this.$el);
      observer.observe();

      if (this.isMagnify) {
        window.$(this.$el).magnificPopup({
          type: 'image'
        });
      }
    }
  }
</script>

<style lang="scss" scoped>
  .lazy-img {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: auto;
    vertical-align: middle;
    object-fit: cover;
  }
</style>
